import { useState } from "react";

import { DRIVE_CONSTANTS } from "@/constants/Drive.constant";
import { driveTypes } from "@/constants/constant-values/default-values.constant";
import { REQUEST_METHODS } from "@/constants/global-constants";
import { useAlterLeadsDriveMutation } from "@/redux/api/prQuery";
import { useAlterProjectMutation } from "@/redux/api/projectsQuery";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";

export type DriveItem = {
  id: string;
  type: "file" | "directory";
};

export type TDriveType = (typeof driveTypes)[keyof typeof driveTypes];

export interface CopyOptions {
  sourceItem: DriveItem;
  sourceDriveType: TDriveType;
  sourceId: string;
  targetId: string;
  targetFolderId?: string;
}

export interface CopyDriveItemsRequest {
  sourceItems: DriveItem[];
  sourceDriveType: TDriveType;
  sourceTypeId: string;
  targetDriveType: TDriveType;
  targetTypeId: string;
  targetDirectoryId?: string;
}

export interface CopyResult {
  success: boolean;
  message?: string;
}

export const useDriveCopy = (): {
  isCopying: boolean;
  copyToTaskAndClaims: (options: CopyOptions) => Promise<CopyResult>;
  copyToProject: (options: CopyOptions) => Promise<CopyResult>;
  copyToProjectRegister: (options: CopyOptions) => Promise<CopyResult>;
} => {
  const [isCopying, setIsCopying] = useState(false);
  const [alterProject] = useAlterProjectMutation();
  const [alterLeadsDrive] = useAlterLeadsDriveMutation();

  const getMutationHandler = (
    targetDriveType: TDriveType,
  ): typeof alterProject | typeof alterLeadsDrive => {
    return targetDriveType === driveTypes.PROJECT ||
      targetDriveType === driveTypes.TASK_AND_CLAIMS
      ? alterProject
      : alterLeadsDrive;
  };

  const copyDriveItems = async (
    options: CopyOptions,
    targetDriveType: TDriveType,
  ): Promise<CopyResult> => {
    setIsCopying(true);

    try {
      const {
        sourceItem,
        sourceDriveType,
        sourceId,
        targetId,
        targetFolderId,
      } = options;

      // Prepare request body
      const requestBody: CopyDriveItemsRequest = {
        sourceItems: [sourceItem],
        sourceDriveType,
        sourceTypeId: sourceId,
        targetDriveType,
        targetTypeId: targetId,
        ...(targetFolderId && { targetDirectoryId: targetFolderId }),
      };

      // Get appropriate mutation handler and parameter key
      const mutationHandler = getMutationHandler(targetDriveType);
      const paramKey =
        targetDriveType === driveTypes.PROJECT_REGISTER
          ? "leadsId"
          : "projectId";

      // Make API request
      const response = await mutationHandler({
        [paramKey]: targetId,
        data: requestBody,
        method: REQUEST_METHODS.POST,
        params: "drive/copy",
      });

      // Handle success using validateResponse
      let success = false;
      validateResponse({
        response,
        onSuccess: () => {
          const successMessage = DRIVE_CONSTANTS.MESSAGES.COPY_SUCCESS;
          customToast.success(successMessage);
          success = true;
        },
        onError: () => {
          throw new Error(DRIVE_CONSTANTS.MESSAGES.COPY_ERROR);
        },
      });

      return {
        success,
        message: success ? DRIVE_CONSTANTS.MESSAGES.COPY_SUCCESS : undefined,
      };
    } catch (error) {
      // Handle error
      const errorMessage =
        error instanceof Error
          ? error.message
          : DRIVE_CONSTANTS.MESSAGES.COPY_ERROR;

      customToast.error(errorMessage);

      return { success: false, message: errorMessage };
    } finally {
      setIsCopying(false);
    }
  };

  const copyToTaskAndClaims = (options: CopyOptions): Promise<CopyResult> =>
    copyDriveItems(options, driveTypes.TASK_AND_CLAIMS);

  const copyToProject = (options: CopyOptions): Promise<CopyResult> =>
    copyDriveItems(options, driveTypes.PROJECT);

  const copyToProjectRegister = (options: CopyOptions): Promise<CopyResult> =>
    copyDriveItems(options, driveTypes.PROJECT_REGISTER);

  return {
    isCopying,
    copyToTaskAndClaims,
    copyToProject,
    copyToProjectRegister,
  };
};

export default useDriveCopy;
